import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditNoteIcon from "@mui/icons-material/EditNote";
import Swal from "sweetalert2";
import moment from "moment-timezone";
import BentoCard from "components/bento/BentoCard";
import StyledDataGrid from "components/bento/StyledDataGrid";
import { GetSalesDocumentList, DiscardSalesDocumentDraft } from "api/salesDocument";

const DOC_TYPE_LABEL = { receipt: "docTypeReceipt", quotation: "docTypeQuotation", invoice: "docTypeInvoice" };

// Saved-but-not-issued documents (status "draft"). Clicking a row hands the
// draft to the parent, which loads it into the Create tab for editing /
// issuing (see scenes/salesDocuments/index.jsx).
const DraftsTab = ({ onOpenDraft }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const refreshList = () => {
    setIsLoading(true);
    GetSalesDocumentList({ status: "draft", pageSize: 100, sort: { field: "updatedAt", sort: "desc" } }).then((res) => {
      setRows(res.success && Array.isArray(res.data) ? res.data : []);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDiscard = async (row) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: t("salesDocuments.discardDraftConfirm"),
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: t("salesDocuments.discardDraft"),
      cancelButtonText: t("common.cancel"),
    });
    if (!confirm.isConfirmed) return;
    const res = await DiscardSalesDocumentDraft(row._id);
    if (!res.success) {
      Swal.fire({ icon: "error", text: res.message });
      return;
    }
    refreshList();
    Swal.fire({ icon: "success", text: t("salesDocuments.draftDiscarded"), timer: 2000, toast: true, position: "top-right", showConfirmButton: false });
  };

  const columns = [
    {
      field: "docType", headerName: t("salesDocuments.historyColumnDocType"), flex: 0.8,
      valueGetter: (params) => t(`salesDocuments.${DOC_TYPE_LABEL[params.row.docType]}`),
    },
    {
      field: "customerName", headerName: t("salesDocuments.historyColumnCustomer"), flex: 1.2,
      valueGetter: (params) => params.row.customer?.name || t("salesDocuments.draftNoCustomer"),
    },
    {
      field: "itemCount", headerName: t("salesDocuments.itemsSection"), flex: 0.5,
      valueGetter: (params) => params.row.items?.length || 0,
    },
    {
      // Items are always THB (see backend/models/SalesDocument.js) — the
      // draft's display currency only applies once the PDF is rendered.
      field: "totalAmount", headerName: t("salesDocuments.historyColumnTotal"), flex: 0.8,
      valueGetter: (params) => `฿${(Number(params.row.totalAmount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
    },
    {
      field: "updatedAt", headerName: t("salesDocuments.draftColumnLastSaved"), flex: 0.9,
      valueGetter: (params) => (params.row.updatedAt ? moment(params.row.updatedAt).tz("Asia/Vientiane").format("DD/MM/YYYY HH:mm") : ""),
    },
    { field: "createdBy", headerName: t("salesDocuments.draftColumnCreatedBy"), flex: 1 },
    {
      field: "actions", headerName: "", width: 100, sortable: false, filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex" }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title={t("salesDocuments.editDraft")}>
            <IconButton size="small" color="secondary" onClick={() => onOpenDraft?.(params.row)}>
              <EditNoteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("salesDocuments.discardDraft")}>
            <IconButton size="small" color="error" onClick={() => handleDiscard(params.row)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return !isLoading && rows.length === 0 ? (
    <BentoCard bodySx={{ p: "48px 22px", textAlign: "center" }}>
      <EditNoteIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
      <Typography color="text.secondary">{t("salesDocuments.noDrafts")}</Typography>
    </BentoCard>
  ) : (
    <Box height="70vh" width="100%">
      <StyledDataGrid
        loading={isLoading}
        getRowId={(row) => row._id}
        rows={rows}
        columns={columns}
        onRowClick={(params) => onOpenDraft?.(params.row)}
        containerSx={{ height: "100%" }}
        autoHeight={false}
      />
    </Box>
  );
};

export default DraftsTab;
